import { z } from "zod";
import { validateGeometry, type Geometry, type LineStringGeometry, type PointGeometry, type PolygonGeometry } from "../geo";
import { isValidOib } from "../oib";
import { isIsoDate } from "../time";
import { CONNECTIONS, LOCALES, PASSWORD_MIN_LENGTH, PUBLIC_AUTHORITY_PAGES, RESERVED_AUTHORITY_SLUGS } from "../constants";

export const idSchema = z.string().uuid();
export const optionalId = idSchema.nullish();
export const isoDateTime = z.string().datetime({ offset: true });
export const isoDate = z.string().refine(isIsoDate, { message: "error.invalidDate" });
export const metres = z.number().finite().min(0).max(10000);
export const positiveMetres = z.number().finite().gt(0).max(1000);
export const cents = z.number().int();
export const nonNegativeCents = z.number().int().min(0);
export const localeSchema = z.enum(LOCALES);
export const connectionsSchema = z.array(z.enum(CONNECTIONS)).max(CONNECTIONS.length);
export const oibSchema = z.string().refine(isValidOib, { message: "error.invalidOib" });
export const optionalOib = z
  .string()
  .transform((s) => s.trim())
  .refine((s) => s === "" || isValidOib(s), { message: "error.invalidOib" })
  .transform((s) => (s === "" ? null : s))
  .nullish();
export const emailSchema = z.string().trim().toLowerCase().email({ message: "error.invalidEmail" }).max(254);
/** Passwords are never trimmed or case-folded. */
export const passwordSchema = z.string().min(PASSWORD_MIN_LENGTH, { message: "error.passwordShort" }).max(200);
export const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .refine((s) => s === "" || z.string().email().safeParse(s).success, { message: "error.invalidEmail" })
  .transform((s) => (s === "" ? null : s))
  .nullish();
export const shortText = z.string().trim().max(200);
export const longText = z.string().trim().max(10000);
export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, { message: "error.validation" })
  .min(2)
  .max(60);
/** Authority slug: the first path segment of its public pages, so app paths are not allowed. */
export const authoritySlugSchema = slugSchema.refine((s) => !RESERVED_AUTHORITY_SLUGS.includes(s), { message: "error.slugReserved" });
/** Port slug: the second path segment, so the authority's own public pages are not allowed. */
export const portSlugSchema = slugSchema.refine((s) => !(PUBLIC_AUTHORITY_PAGES as readonly string[]).includes(s), { message: "error.slugReserved" });

function geometrySchema<G extends Geometry>(type: G["type"] | G["type"][]) {
  return z.custom<G>((value) => validateGeometry(value, type) === null, {
    // Detailed message is produced by `validateGeometry` in the API layer (see geometryIssue).
    message: "geo.error.invalid",
  });
}
export const pointSchema = geometrySchema<PointGeometry>("Point");
export const lineSchema = geometrySchema<LineStringGeometry>("LineString");
export const polygonSchema = geometrySchema<PolygonGeometry>("Polygon");
export const berthGeometrySchema = geometrySchema<PointGeometry | PolygonGeometry>(["Point", "Polygon"]);

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export function slugify(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}
